import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAcceptInvitationMutation } from "@/store/rest_api";
import { authService } from "@/services/authService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Mail } from "lucide-react";
import React from "react";

export default function AcceptInvitation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [acceptInvitation] = useAcceptInvitationMutation();
  const [status, setStatus] = useState<"loading" | "success" | "error" | "auth">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  // Where to send the user back to after they sign in / register.
  const returnTo = encodeURIComponent(`/accept-invitation?token=${token ?? ""}`);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("This invitation link is invalid or incomplete.");
      return;
    }

    // The accept endpoint requires an authenticated user. If the invitee isn't signed in, prompt them
    // to log in or create an account (with the invited email) first, then return here to complete.
    if (!authService.getBearerToken()) {
      setStatus("auth");
      return;
    }

    const accept = async () => {
      try {
        await acceptInvitation(token).unwrap();
        setStatus("success");
        setTimeout(() => navigate("/dashboard"), 2000);
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(
          error?.detail ||
            error?.data?.detail ||
            "We couldn't accept this invitation. It may have expired, already been used, or been sent to a different email than the one you're signed in with.",
        );
      }
    };

    accept();
  }, [token, acceptInvitation, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Team Invitation</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {status === "loading" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-muted-foreground">Accepting invitation...</p>
            </>
          )}

          {status === "auth" && (
            <>
              <Mail className="h-12 w-12 text-primary" />
              <p className="text-lg font-semibold">You're invited!</p>
              <p className="text-muted-foreground text-center">
                Sign in (or create an account using the email this invitation was sent to) to join the team.
              </p>
              <div className="flex gap-2 pt-1">
                <Button onClick={() => navigate(`/login?redirect=${returnTo}`)}>Log in</Button>
                <Button variant="outline" onClick={() => navigate(`/register?memberInvite=${token}`)}>
                  Create account
                </Button>
              </div>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="text-lg font-semibold">Welcome to the team!</p>
              <p className="text-muted-foreground text-center">
                You've successfully joined the organization. Redirecting to dashboard...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="h-12 w-12 text-destructive" />
              <p className="text-lg font-semibold">Something went wrong</p>
              <p className="text-muted-foreground text-center">{errorMessage}</p>
              <Button onClick={() => navigate("/login")}>Go to Login</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
